import styled from "styled-components";
import {
  AgencyTags,
  Card,
  CardSiderFlexContent,
  ScrollingTextArea,
} from "@bagel-web/components";
import { useQuery } from "@tanstack/react-query";
import {
  CommonAPI,
  CommonContentMessage,
  CommonFnCallMessage,
  CommonServiceInteractionEventItem,
} from "../types";
import { eventStreamQueries } from "../queries";
import {
  isCreatedAgent,
  isGameEvent,
  isServiceInteraction,
} from "../util/momentEventItem";
import FnCallMessage from "../session/message/FnCallMessage";
import { Badge } from "react-bootstrap";
import ContentMessage from "../session/message/ContentMessage";
import { isGeneratedContent } from "../util/generatedContent";
import LLMEventRating from "../LLMEventRating";

type ServiceInteractionKind =
  CommonServiceInteractionEventItem["event"]["kind"];

// The interaction kinds whose outcome a user rates: an LLM call's chalk log.
const RATEABLE_KINDS: ReadonlyArray<ServiceInteractionKind> = [
  "generate_function_call",
  "generate_text",
  "generate_json",
];

const isRateable = (kind: ServiceInteractionKind) =>
  RATEABLE_KINDS.includes(kind);

const MomentSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AgentRecentActionCard = ({
  sessionId,
  agentId,
  momentId,
  commonAPI,
}: {
  sessionId: string;
  agentId: string;
  momentId: number | null;
  commonAPI: CommonAPI;
}) => {
  const { data: momentDetail } = useQuery({
    queryKey: eventStreamQueries.getDetail(sessionId, agentId, momentId),
    queryFn: () =>
      momentId !== null
        ? commonAPI.fetchEventStreamDetail(sessionId, agentId, momentId)
        : null,
    staleTime: Infinity,
  });

  const llmResult = momentDetail?.event_wrappers.find(isServiceInteraction);

  const llmErrorMessage = llmResult?.event.error_message || null;

  const agentCreation = momentDetail?.event_wrappers.find(isCreatedAgent);

  const gameEvents = momentDetail?.event_wrappers.filter(isGameEvent);

  const functionCallMessage = gameEvents
    ?.map((eventItem) => eventItem.event)
    .find(
      (message): message is CommonFnCallMessage =>
        message.message_type === "FnCallMessage"
    );

  const generatedContentMessage = gameEvents
    ?.map((eventItem) => eventItem.event)
    .find(
      (message): message is CommonContentMessage =>
        message.message_type === "ContentMessage" && isGeneratedContent(message)
    );

  return (
    <Card
      Sider={() => (
        <CardSiderFlexContent $collapsed={false}>
          <h5>Moment Summary</h5>
          {llmErrorMessage && <Badge bg="danger">Error</Badge>}
          {llmResult &&
            !llmErrorMessage &&
            momentId &&
            isRateable(llmResult.event.kind) && (
              <LLMEventRating
                sessionId={sessionId}
                agentId={agentId}
                momentId={momentId}
                eventId={llmResult.id}
                isPositive={llmResult.rating?.is_positive}
                rationale={llmResult.rating?.rationale}
                commonAPI={commonAPI}
              />
            )}
        </CardSiderFlexContent>
      )}
    >
      <MomentSummaryContainer>
        <div className="d-flex flex-row gap-2 align-items-start">
          <ScrollingTextArea
            className="flex-grow-1"
            $maxHeight="200px"
            $resize={false}
          >
            {agentCreation && "Agent created"}
            {functionCallMessage && momentDetail && (
              <>
                <FnCallMessage
                  moment={momentDetail}
                  message={functionCallMessage}
                />
              </>
            )}
            {generatedContentMessage && momentDetail && (
              <ContentMessage
                moment={momentDetail}
                message={generatedContentMessage}
              />
            )}
            {llmErrorMessage && <code>{llmErrorMessage}</code>}
          </ScrollingTextArea>
          {functionCallMessage && (
            <AgencyTags tags={functionCallMessage.agency_tags} />
          )}
        </div>
      </MomentSummaryContainer>
    </Card>
  );
};

export default AgentRecentActionCard;
